/**
 * NotificationBell — header widget that shows a badge with unread count and
 * opens a dropdown with a mixed feed. Polls every 60s.
 *
 * Only shown for admin / manager / member (not clients).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Bell, CalendarBlank, ChatCircleDots, Clipboard, Envelope, Megaphone, Warning, X, UserMinus } from "@phosphor-icons/react";

const KIND_META = {
  overdue_task:     { icon: Warning,       color: "text-red-600",     bg: "bg-red-50 border-red-200" },
  leave_request:    { icon: CalendarBlank, color: "text-blue-600",    bg: "bg-blue-50 border-blue-200" },
  leave_decision:   { icon: CalendarBlank, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-200" },
  broadcast:        { icon: Megaphone,     color: "text-amber-600",   bg: "bg-amber-50 border-amber-200" },
  chat_unread:      { icon: ChatCircleDots, color: "text-purple-600", bg: "bg-purple-50 border-purple-200" },
  client_request:   { icon: Clipboard,     color: "text-orange-700",  bg: "bg-orange-50 border-orange-200" },
  builder_inquiry:  { icon: Envelope,      color: "text-amber-700",   bg: "bg-amber-50 border-amber-200" },
  site_visit_request: { icon: CalendarBlank, color: "text-sky-700",   bg: "bg-sky-50 border-sky-200" },
  missed_checkin:   { icon: UserMinus,     color: "text-red-700",     bg: "bg-red-50 border-red-200" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [feed, setFeed] = useState({ items: [], unread_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const canSee = user && user.role !== "client";

  const poll = useCallback(async () => {
    if (!canSee) return;
    try {
      const { data } = await http.get("/notifications");
      setFeed(data);
    } catch (err) { /* silent */ }
  }, [canSee]);

  useEffect(() => {
    if (!canSee) return;
    poll();
    const t = setInterval(poll, 60_000);
    return () => clearInterval(t);
  }, [canSee, poll]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!canSee) return null;

  const badge = Math.min(feed.unread_count || 0, 99);

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        data-testid="notification-bell-button"
        className="relative h-9 w-9 flex items-center justify-center border border-zinc-400 rounded-none hover:bg-zinc-100 transition-colors"
      >
        <Bell size={16} weight="bold" />
        {badge > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-mono font-black tracking-wide flex items-center justify-center border border-white"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 w-[380px] max-h-[520px] overflow-y-auto bg-white border-2 border-zinc-900 shadow-brutal z-50"
        >
          <div className="flex items-center justify-between p-3 border-b-2 border-zinc-900 sticky top-0 bg-white">
            <div className="font-display text-lg font-black tracking-tighter uppercase flex items-center gap-2">
              <Bell size={14} weight="bold" /> Notifications
            </div>
            <div className="flex items-center gap-2">
              <span className="label-overline text-zinc-500" data-testid="notification-total">
                {feed.unread_count} unread
              </span>
              <button onClick={() => setOpen(false)} aria-label="Close"
                      className="p-1 hover:bg-zinc-100">
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>

          {feed.items.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">
              You're all caught up. No new notifications.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-200">
              {feed.items.map((n) => {
                const meta = KIND_META[n.kind] || { icon: Bell, color: "text-zinc-600", bg: "bg-zinc-50 border-zinc-200" };
                const Ico = meta.icon;
                const inner = (
                  <div className={`p-3 flex items-start gap-3 border-l-4 ${meta.bg} hover:bg-zinc-100 transition-colors`}>
                    <div className={`${meta.color} mt-0.5 shrink-0`}>
                      <Ico size={16} weight="bold" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate" data-testid={`notif-title-${n.id}`}>
                        {n.title}
                      </div>
                      {n.subtitle && (
                        <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{n.subtitle}</div>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400 shrink-0 pt-1">
                      {timeAgo(n.created_at)}
                    </div>
                  </div>
                );
                return (
                  <li key={n.id} data-testid={`notif-item-${n.id}`}>
                    {n.href ? (
                      <Link to={n.href} onClick={() => setOpen(false)} className="block">{inner}</Link>
                    ) : (
                      inner
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
